/**
 * Leitura da versão do widget reportada por cada máquina.
 *
 * A régua é a **maior versão observada na frota**, não uma constante. Guardar "a versão mais nova"
 * num appsettings ou numa env var cria um segundo lugar para a verdade, e o modo de falha é o pior
 * possível — silencioso e invertido: constante esquecida no bump deixa a frota inteira "em dia" no
 * dia seguinte a uma release; constante adiantada pinta todo mundo de vermelho sem motivo.
 * Derivando do máximo observado, a régua se move sozinha quando a primeira máquina atualiza.
 */

/** Máquina sem sinal há mais que isto não é cobrada de estar atualizada — pode só estar desligada. */
export const STALE_DAYS = 14;

/**
 * Compara duas versões como semver numérico. `"0.1.10" < "0.1.9"` em ordem alfabética, e o widget
 * chega na 0.1.10 em poucas releases — comparar como texto é um bug com data marcada, e daqueles
 * que ninguém percebe, porque a tela só fica sutilmente errada.
 *
 * Devolve > 0 se `a` for mais nova, < 0 se mais velha, 0 se iguais.
 */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string) => v.trim().replace(/^v/i, "").split(".").map((p) => parseInt(p, 10));
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i];
    const y = pb[i];
    if (isNaN(x) || isNaN(y)) return 0; // malformada: não dá para ordenar, então não julga
    if (x !== y) return x - y;
  }
  return 0;
}

const isValid = (v: string | null | undefined): v is string =>
  !!v && /^v?\d+(\.\d+)*$/.test(v.trim());

/** A maior versão que alguma máquina reportou — a régua da frota. */
export function fleetLatest(versions: (string | null | undefined)[]): string | null {
  return versions.filter(isValid).reduce<string | null>(
    (max, v) => (max === null || compareVersions(v, max) > 0 ? v : max),
    null
  );
}

const daysSince = (iso: string) => (Date.now() - new Date(iso).getTime()) / 86_400_000;

export type WidgetStatus =
  | "atualizado"
  | "desatualizado"
  | "sem-sinal"
  | "nao-reportou"
  | "falha-update"
  | "aguardando-reinicio";

/**
 * O estado de uma máquina. Versão sozinha mente por omissão: "0.1.4" pode ser um updater quebrado
 * (problema) ou o notebook de alguém de férias (não-problema) — só a data do último contato separa
 * os dois, e são ações completamente diferentes.
 */
export function widgetStatus(
  version: string | null | undefined,
  lastSeenAt: string | null | undefined,
  latest: string | null,
  updateStatus?: string | null
): WidgetStatus {
  if (!isValid(version)) return "nao-reportou";
  if (lastSeenAt && daysSince(lastSeenAt) > STALE_DAYS) return "sem-sinal";

  const atrasada = !!latest && compareVersions(version, latest) < 0;

  // O MOTIVO vence o sintoma. "Desatualizado" só diz que a máquina está atrás; "falha no update" e
  // "aguardando reinício" dizem POR QUE — e levam a ações diferentes (chamado × um clique).
  // Só valem quando a máquina está de fato atrás: updater com erro numa máquina já na última
  // versão é ruído de uma checagem que falhou e não impede nada.
  if (atrasada && updateStatus === "error") return "falha-update";
  if (atrasada && updateStatus === "downloaded") return "aguardando-reinicio";

  return atrasada ? "desatualizado" : "atualizado";
}

export const WIDGET_STATUS_META: Record<WidgetStatus, { label: string; chip: string }> = {
  atualizado: {
    label: "em dia",
    chip: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  },
  desatualizado: {
    label: "desatualizado",
    chip: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
  "sem-sinal": {
    label: `sem sinal há +${STALE_DAYS}d`,
    chip: "bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300",
  },
  "nao-reportou": {
    label: "não reportou",
    chip: "bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300",
  },
  // Estes dois substituem "desatualizado" quando a máquina disse o motivo.
  "falha-update": {
    label: "falha no update",
    chip: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  },
  "aguardando-reinicio": {
    label: "aguardando reinício",
    chip: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  },
};

/** "há 2 h" / "há 3 d" — o suficiente para saber se o dado é fresco. */
export function lastSeenLabel(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const horas = (Date.now() - d.getTime()) / 3_600_000;
  if (horas < 1) return "agora há pouco";
  if (horas < 24) return `há ${Math.floor(horas)} h`;
  return `há ${Math.floor(horas / 24)} d`;
}
